import * as React from 'react'

import { PlusIcon } from 'lucide-react'
import { MobXProviderContext, observer } from 'mobx-react'
import { Controller, useWatch } from 'react-hook-form'

import ColorPalette from '~/client/src/shared/components/ColorPicker/ColorPalette'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { LibraryIconColorHint } from '~/client/src/shared/components/IconPicker'
import InitialState from '~/client/src/shared/stores/InitialState'
import {
  DEFAULT_SITEMAP_ITEM_COLOR,
  SITEMAP_ITEMS_COLORS_VALUES,
} from '~/client/src/shared/utils/SitemapItemsColors'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../shadcn-ui/popover'
import { ColorFieldConfig } from '../types'

interface ColorFieldProps {
  field: ColorFieldConfig
  control: any
  error?: string
}

const ColorField: React.FC<ColorFieldProps> = ({ field, control, error }) => {
  const [open, setOpen] = React.useState(false)
  const [isCustom, setIsCustom] = React.useState(false)
  const { state } = React.useContext(MobXProviderContext) as {
    state?: InitialState
  }
  const iconName: string | null = useWatch({
    control,
    name: field.iconFieldName,
  })

  // Base palette + the project's custom colors (deduped), matching the map
  // picker. Read straight from the observable (the component is an observer) so
  // colors added/loaded after mount appear without waiting for a remount.
  const colors = Array.from(
    new Set([
      ...SITEMAP_ITEMS_COLORS_VALUES,
      ...(state?.projectColoringOptions?.colors || []),
    ]),
  )

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setIsCustom(false)
  }

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={field.defaultValue ?? undefined}
      render={({ field: { onChange, value } }) => {
        const current = value || DEFAULT_SITEMAP_ITEM_COLOR
        const selectColor = (color: string) => {
          onChange(color)
          handleOpenChange(false)
        }
        return (
          <div className="tw:flex tw:flex-col tw:gap-1">
            <div className="tw:flex tw:items-center tw:gap-1">
              <span className="tw:text-body-sm tw:font-medium tw:text-muted-foreground">
                {field.label}
              </span>
              <LibraryIconColorHint iconName={iconName} />
            </div>
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger
                disabled={field.disabled || field.readOnly || field.displayOnly}
                render={
                  <FieldTrigger
                    aria-label={field.label}
                    displayOnly={field.displayOnly}
                  />
                }
              >
                <span
                  className="tw:block tw:h-4 tw:rounded-sm"
                  style={{ backgroundColor: current }}
                />
              </PopoverTrigger>
              <PopoverContent className="tw:w-auto">
                {isCustom ? (
                  <ColorPalette
                    value={current}
                    onChange={color => color && onChange(color)}
                    onClose={() => setIsCustom(false)}
                  />
                ) : (
                  <div className="tw:grid tw:grid-cols-5 tw:gap-2">
                    {colors.map(color => (
                      <button
                        key={color}
                        type="button"
                        aria-label={color}
                        onClick={() => selectColor(color)}
                        className={`tw:size-7 tw:rounded tw:border tw:border-solid tw:cursor-pointer ${
                          current === color
                            ? 'tw:border-primary tw:ring-2 tw:ring-primary/40'
                            : 'tw:border-border'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <button
                      type="button"
                      aria-label="Add custom color"
                      onClick={() => setIsCustom(true)}
                      className="tw:flex tw:size-7 tw:items-center tw:justify-center tw:rounded tw:border tw:border-solid tw:border-border tw:text-muted-foreground tw:cursor-pointer tw:hover:bg-muted"
                    >
                      <PlusIcon size={16} />
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {error && (
              <span className="tw:text-body-sm tw:text-destructive">
                {error}
              </span>
            )}
          </div>
        )
      }}
    />
  )
}

export default observer(ColorField)
